import auth from '@/auth/auth';
import {
    AllocationPhaseDeleteInput,
    AllocationPhaseInput,
    AllocationPhaseUpdateInput,
    AllocationPhaseUpdateInputFlat,
    AllocationUsage,
    CopyTargetLocation,
    TimelineVariant,
} from '@/components/Timeline/allocation/types';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import { validateAllocationUpdateResponse } from '@/components/Timeline/timeline';
import { useTimelineStore } from '@/components/Timeline/Timeline.store';
import { AllocationPhaseUpdate, LocationTypeCategory } from '@/components/Timeline/Timeline.types';
import {
    CreateExternalEventAllocationDocument,
    ExternalEventAllocationDuplicateDocument,
    ExternalEventAllocationUpdateAndDuplicateDocument,
    ExternalEventAllocationUpdateDocument,
    ExternalEventVariantStatus,
    GetExternalEventAllocationsForEventDocument,
    GetExternalEventDataForTimelineDocument,
    TimelineExternalEventFragment,
    TimelineExternalEventLocationFragment,
    UpdateExternalEventReferenceAllocationIdDocument,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { insideRangeCheck } from '@/utils/date';
import { useApolloClient, useMutation, useQuery } from '@vue/apollo-composable';
import { DateTime } from 'luxon';
import { computed, Ref, ref, watch } from 'vue';
import {
    ExternalEventTimelineLocation,
    getStatusFromVisibility,
    mapExternalEventAllocationForTimeline,
    mapExternalEventVariantForTimeline,
} from '../externalEvent';

const ACTIVE_STATES = ['fix', 'option'] as ExternalEventVariantStatus[];

export function useExternalEventEventTimelineData(
    eventId: Ref<string>,
    timelineStart: Ref<DateTime | undefined>,
    timelineEnd: Ref<DateTime | undefined>,
    dateTimeFromRoute: Ref<DateTime | undefined>,
    setSelectedStartDateTime: (value: DateTime) => { newTimelineStart: DateTime; newTimelineEnd: DateTime },
) {
    const variantsRef: Ref<TimelineVariant[]> = ref([]);
    let variantsManuallyUpdated = false;
    const event: Ref<TimelineExternalEventFragment | undefined> = ref();
    const locations: Ref<ExternalEventTimelineLocation[]> = ref([]);
    const defaultPhaseTypeId = ref('');
    const executionPhasePosition = ref(1);
    const usages: Ref<AllocationUsage[]> = ref([]);

    const client = useApolloClient().client;
    const store = useTimelineStore();
    const timelineDataProcessing = ref(false);

    const { loading, error, onResult } = useQuery(
        GetExternalEventDataForTimelineDocument,
        () => ({
            id: eventId.value,
            activeStates: ACTIVE_STATES,
        }),
        () => ({
            enabled: eventId.value !== undefined,
            fetchPolicy: 'network-only',
        }),
    );

    function mapToTimelineLocation(location: TimelineExternalEventLocationFragment): ExternalEventTimelineLocation {
        return {
            id: location.id,
            label: location.name,
            status: getStatusFromVisibility(location.visibility.nodes[0]?.visibility),
        };
    }

    const areAllocationsEditable = auth.isAdmin();

    onResult((result) => {
        const data = result.data;
        if (data) {
            timelineDataProcessing.value = true;

            try {
                let start = timelineStart.value;
                let end = timelineEnd.value;
                // only set start/end DateTime if there is none initially set
                if (start === undefined && end === undefined) {
                    // start default now only if nothing else is available for calculation
                    let initialStart: DateTime = DateTime.now();
                    if (dateTimeFromRoute.value) {
                        initialStart = dateTimeFromRoute.value;
                    } else {
                        const allocationStart = result.data.firstAllocation?.nodes.map((n) =>
                            DateTime.fromISO(n.start),
                        )[0];
                        if (allocationStart && allocationStart.isValid) {
                            initialStart = allocationStart;
                        } else {
                            if (result.data.externalEvent?.year) {
                                const yearStart = DateTime.fromObject({ year: result.data.externalEvent.year });
                                if (yearStart.isValid) {
                                    initialStart = yearStart;
                                }
                            }
                        }
                    }
                    const { newTimelineStart, newTimelineEnd } = setSelectedStartDateTime(initialStart);
                    start = newTimelineStart;
                    end = newTimelineEnd;
                }

                event.value = data.externalEvent ?? undefined;
                const variants = data.externalEvent?.variants.nodes;

                if (!variants) {
                    throw Error('External event does not contain variants');
                }

                const allocations = data.externalEventAllocations?.nodes ?? [];
                const ids = [...allocations.map((a) => a.locationId)];
                const countsMap = ids.reduce(
                    (map, id) => map.set(id, (map.get(id) ?? 0) + 1),
                    new Map<string, number>(),
                );
                const referenceAllocationIds = variants.flatMap((v) => v.referenceExternalEventAllocationId ?? []);
                countsMap.set('reference-allocation-row', referenceAllocationIds.length);
                store.setLocationAllocationTotalCounts(countsMap);

                if (!variantsManuallyUpdated) {
                    variantsRef.value = variants.map((variant) =>
                        mapExternalEventVariantForTimeline(
                            variant,
                            start,
                            end,
                            executionPhasePosition.value,
                            areAllocationsEditable,
                        ),
                    );
                }

                if (data.externalEvent?.series.national === false && data.externalEvent?.location) {
                    locations.value = [mapToTimelineLocation(data.externalEvent.location)];
                } else {
                    locations.value =
                        data.locationsNational?.nodes.map((location) => mapToTimelineLocation(location)) ?? [];
                }
                const externalEvent = data.externalEvent;
                if (!externalEvent) {
                    throw new Error('Missing external event data');
                }
                usages.value =
                    data.usages?.nodes.map((u) => ({
                        ...u,
                        type: u.type,
                        color: mapToPhaseColor(u.type, externalEvent.series.category),
                    })) ?? [];
                const executionPhase = data.usages?.nodes.find((u) => u.isExecution);
                if (!executionPhase) {
                    throw Error('No execution phase exists. Wrong data');
                }
                defaultPhaseTypeId.value = executionPhase.id;
                executionPhasePosition.value = executionPhase.position;
            } finally {
                timelineDataProcessing.value = false;
            }
        }
    });

    watch([timelineStart, timelineEnd], async ([newStart, newEnd]) => {
        const start = timelineStart.value;
        const end = timelineEnd.value;

        if (start && end && newStart && newEnd) {
            if (!event.value) {
                throw new Error('Event not loaded');
            }
            timelineDataProcessing.value = true;
            try {
                const currentEvent = event.value;

                const result = await client.query({
                    query: GetExternalEventAllocationsForEventDocument,
                    variables: { id: eventId.value },
                });
                const allocations = result.data.externalEventAllocations?.nodes;

                if (allocations) {
                    // update timeline range filtering
                    variantsRef.value = variantsRef.value.map((v) => ({
                        ...v,
                        allocations: {
                            nodes: allocations
                                .filter(
                                    (n) =>
                                        n.variant.id === v.id &&
                                        insideRangeCheck(
                                            DateTime.fromISO(n.start),
                                            DateTime.fromISO(n.end),
                                            newStart,
                                            newEnd,
                                        ),
                                )
                                .map((allocation) =>
                                    mapExternalEventAllocationForTimeline(
                                        {
                                            ...v,
                                            event: {
                                                id: currentEvent.id,
                                                nameShort: currentEvent.nameShort,
                                            },
                                        },
                                        allocation,
                                        'ExternalLocation',
                                        executionPhasePosition.value,
                                        areAllocationsEditable,
                                        currentEvent.series.category,
                                    ),
                                ),
                        },
                    }));
                }
            } finally {
                timelineDataProcessing.value = false;
            }
        }
    });

    function setVariantsRef(value: TimelineVariant[]) {
        variantsManuallyUpdated = true;
        variantsRef.value = value;
    }

    const timelineHeaderName = computed(() => {
        const national = event.value?.series.national ? 'National' : 'International';
        const year = event.value?.year;
        const category = $t('external-event-series-category', { category: event.value?.series.category ?? '' });
        return `${category} ${national} ${year}`;
    });

    const timelineHeaderLinkTarget = computed(() => {
        return { name: 'external-event-event', params: { eventId: event.value?.id } };
    });

    function setLocations(value: ExternalEventTimelineLocation[]) {
        locations.value = value;
    }

    const updateReferenceAllocationMutation = useMutation(UpdateExternalEventReferenceAllocationIdDocument, {
        fetchPolicy: 'no-cache',
    });

    async function updateReferenceAllocationId(variantId: string, referenceAllocationId: string | null) {
        await updateReferenceAllocationMutation.mutate({
            variantId,
            referenceAllocationId,
        });
        const vref = variantsRef.value.map((v) => {
            if (v.id !== variantId) return v;

            return {
                ...v,
                referenceAllocationId,
                allocations: {
                    nodes: v.allocations.nodes.map((a) => ({
                        ...a,
                        allocationVariant: {
                            ...a.allocationVariant,
                            referenceAllocationId,
                        },
                    })),
                },
            };
        });
        setVariantsRef(vref);
    }

    const createAllocationMutation = useMutation(CreateExternalEventAllocationDocument, { fetchPolicy: 'no-cache' });

    async function createAllocation(
        variantId: string,
        phases: AllocationPhaseInput[],
        locationId: string,
        startDateTime: DateTime,
        endDateTime: DateTime,
        locationTypeCategory: LocationTypeCategory,
    ) {
        const start = startDateTime.toISO();
        if (!start) {
            throw Error('Conversion toISO() failed for startDateTime');
        }

        const end = endDateTime.toISO();
        if (!end) {
            throw Error('Conversion toISO() failed for endDateTime');
        }

        const result = await createAllocationMutation?.mutate({
            variantId,
            locationId,
            start,
            end,
            phases,
        });

        if (result?.errors) {
            throw new Error('Could not create allocation');
        }

        if (!event.value) {
            throw new Error('Event not loaded');
        }

        const currentEvent = event.value;

        if (result?.data) {
            const resultAllocation = result.data.create?.allocation;
            if (resultAllocation) {
                store.incrementLocationAllocationTotalCount(resultAllocation.locationId);
                const updatedVariants = variantsRef.value.map((v) => {
                    if (v.id === variantId) {
                        return {
                            ...v,
                            allocations: {
                                nodes: [
                                    ...v.allocations.nodes,
                                    mapExternalEventAllocationForTimeline(
                                        {
                                            ...v,
                                            event: {
                                                id: currentEvent.id,
                                                nameShort: currentEvent.nameShort,
                                            },
                                        },
                                        resultAllocation,
                                        locationTypeCategory,
                                        executionPhasePosition.value,
                                        areAllocationsEditable,
                                        currentEvent.series.category,
                                    ),
                                ],
                            },
                        };
                    }
                    return v;
                });
                setVariantsRef(updatedVariants);
                return { createdAllocationId: resultAllocation.id, updatedVariantsRef: updatedVariants };
            }
        }
    }

    const updateAllocationMutation = useMutation(ExternalEventAllocationUpdateDocument, { fetchPolicy: 'no-cache' });

    async function updateAllocationData(
        variantId: string,
        allocationId: string,
        _locationTypeCategory: LocationTypeCategory,
        start: DateTime,
        end: DateTime,
        isOptional: boolean,
        createPhases: AllocationPhaseInput[],
        updatePhases: AllocationPhaseUpdateInput[],
        deletePhases: AllocationPhaseDeleteInput[],
        remainingPhases: (AllocationPhaseUpdate & { dayOffsetCount: number })[],
        referenceAllocationId?: string | null,
    ) {
        if (referenceAllocationId !== undefined) {
            const currentVariants = variantsRef.value;
            const variantToUpdate = currentVariants.find((variant) => variant.id === variantId);
            if (variantToUpdate) {
                await updateReferenceAllocationId(variantId, referenceAllocationId);
            }
        }
        const response = await updateAllocationMutation.mutate({
            allocationId,
            start: start.toISO(),
            end: end.toISO(),
            isOptional: isOptional,
            createPhases,
            updatePhases,
            deletePhases,
        });

        if (!event.value) {
            throw new Error('Event not loaded');
        }

        const currentEvent = event.value;

        if (response && response.data) {
            const currentVariants = variantsRef.value;
            const updatedAllocation = response.data.update?.allocation;
            if (updatedAllocation) {
                const confirmedPhases = validateAllocationUpdateResponse(updatedAllocation, remainingPhases);
                if (isOptional !== updatedAllocation.isOptional) {
                    throw Error(`isOptional of allocation ${updatedAllocation.id} not updated correctly`);
                }
                const updatedVariants = currentVariants.map((v) => {
                    if (v.id === variantId) {
                        return {
                            ...v,
                            allocations: {
                                nodes: v.allocations.nodes.map((n) => {
                                    if (n.id === updatedAllocation.id) {
                                        return mapExternalEventAllocationForTimeline(
                                            {
                                                ...v,
                                                referenceAllocationId:
                                                    updatedAllocation.variant.referenceExternalEventAllocationId ??
                                                    null,
                                                event: {
                                                    id: currentEvent.id,
                                                    nameShort: currentEvent.nameShort,
                                                },
                                            },
                                            updatedAllocation,
                                            'ExternalLocation',
                                            executionPhasePosition.value,
                                            areAllocationsEditable,
                                            currentEvent.series.category,
                                        );
                                    }
                                    return n;
                                }),
                            },
                        };
                    } else {
                        return v;
                    }
                });
                setVariantsRef(updatedVariants);

                return {
                    phaseUpdates: confirmedPhases,
                    isOptional,
                    referenceAllocationId: referenceAllocationId ?? null,
                };
            }
        }
        throw Error('Error on ExternalEventAllocation update without duplicate');
    }

    const duplicateAllocationMutation = useMutation(ExternalEventAllocationDuplicateDocument, {
        fetchPolicy: 'no-cache',
    });

    async function duplicateAllocation(
        variantId: string,
        allocationId: string,
        newLocations: CopyTargetLocation[],
        _locationTypeCategory: LocationTypeCategory,
    ) {
        const response = await duplicateAllocationMutation.mutate({
            variantId,
            allocationId,
            locationIds: newLocations.map((target) => target.id),
        });

        if (!response) {
            throw new Error('Unknown locationTypeCategory on duplicateAllocation');
        }

        if (response.errors) {
            throw new Error(`Duplicating allocations failed ${JSON.stringify(response.errors, null, 2)}`);
        }

        const updatedVariant = response.data?.duplicate?.variant;
        if (updatedVariant) {
            for (const { id } of newLocations) {
                store.incrementLocationAllocationTotalCount(id);
            }
            const newValue = variantsRef.value.map((v) => {
                if (v.id === updatedVariant.id) {
                    return mapExternalEventVariantForTimeline(
                        updatedVariant,
                        timelineStart.value,
                        timelineEnd.value,
                        executionPhasePosition.value,
                        areAllocationsEditable,
                    );
                }
                return v;
            });
            setVariantsRef(newValue);
        }
    }

    const duplicateAndUpdateAllocationMutation = useMutation(ExternalEventAllocationUpdateAndDuplicateDocument, {
        fetchPolicy: 'no-cache',
    });

    async function duplicateAndUpdateAllocation(
        variantId: string,
        allocationId: string,
        newLocations: CopyTargetLocation[],
        _locationTypeCategory: LocationTypeCategory,
        start: DateTime,
        end: DateTime,
        isOptional: boolean,
        createPhases: AllocationPhaseInput[],
        updatePhases: AllocationPhaseUpdateInputFlat[],
        deletePhases: AllocationPhaseDeleteInput[],
        referenceAllocationId?: string | null,
    ) {
        if (referenceAllocationId !== undefined) {
            const currentVariants = variantsRef.value;
            const variantToUpdate = currentVariants.find((variant) => variant.id === variantId);
            if (variantToUpdate) {
                await updateReferenceAllocationId(variantId, referenceAllocationId);
            }
        }

        const response = await duplicateAndUpdateAllocationMutation.mutate({
            variantId,
            allocationId,
            updateAllocation: {
                start: start.toISO(),
                end: end.toISO(),
                isOptional,
            },
            createPhases,
            updatePhases,
            deletePhases: deletePhases.map((p) => p.id),
            locationIds: newLocations.map((target) => target.id),
        });

        if (!response) {
            throw new Error('Unknown locationTypeCategory on duplicateAndUpdateAllocation');
        }

        if (response.errors) {
            throw new Error(
                `Duplicating allocations and updating phases failed ${JSON.stringify(response.errors, null, 2)}`,
            );
        }

        const updatedVariant = response.data?.updateAndDuplicate?.variant;

        if (updatedVariant) {
            for (const { id } of newLocations) {
                store.incrementLocationAllocationTotalCount(id);
            }
            const mappedUpdatedVariant = mapExternalEventVariantForTimeline(
                updatedVariant,
                timelineStart.value,
                timelineEnd.value,
                executionPhasePosition.value,
                areAllocationsEditable,
            );
            const newValue = variantsRef.value.map((v) => (v.id === updatedVariant.id ? mappedUpdatedVariant : v));
            setVariantsRef(newValue);
            return updatedVariant.allocations.nodes.find((n) => n.id === allocationId);
        }
        return undefined;
    }

    const timelineLoading = computed(() => {
        return loading.value || timelineDataProcessing.value;
    });

    return {
        variantsRef,
        setVariantsRef,
        timelineLoading,
        error,
        onResult,
        event,
        locations,
        timelineHeaderName,
        timelineHeaderLinkTarget,
        defaultPhaseTypeId,
        executionPhasePosition,
        usages,
        setLocations,
        updateReferenceAllocationId,
        createAllocation,
        updateAllocationData,
        duplicateAllocation,
        duplicateAndUpdateAllocation,
    };
}
